import { db, SYNC_STATUS, LOCAL_WORKSPACE_TENANT_ID } from '../lib/database';
import { outboxManager } from '../lib/sync/OutboxManager';
import { assertMutable } from '../lib/collaboration/ConflictResolver';

/**
 * FormService — multi-tenant CRUD over Dexie with outbox-integrated writes.
 *
 * Security invariants (Track 8 hardening):
 *  - EVERY query is scoped by tenantId. A missing tenant context falls back
 *    to the Local Workspace pseudo-tenant rather than an unscoped scan.
 *  - Every mutation is wrapped in ONE Dexie transaction together with its
 *    outbox delta so a crash can never produce a record without a delta.
 *  - Issued certificates are immutable: edits throw and must go through the
 *    amendment/revision flow (see ConflictResolver.assertMutable).
 */

// UUID v4 generator with a crypto.getRandomValues fallback for non-secure
// contexts (http:// LAN deployments of PocketBase).
function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
}

/** Strip DOM/XSS sinks before persisting user-controlled strings. */
function sanitizeText(value) {
  if (typeof value !== 'string') return value;
  return value
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on(error|load|click)\s*=/gi, '');
}

function sanitizeDeep(data) {
  if (typeof data === 'string') return sanitizeText(data);
  if (Array.isArray(data)) return data.map(sanitizeDeep);
  if (data && typeof data === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(data)) {
      if (k.startsWith('$')) continue; // engine bookkeeping never round-trips
      out[k] = sanitizeDeep(v);
    }
    return out;
  }
  return data;
}

export class FormService {
  /**
   * Save or update a form. Creates enqueue a CREATE delta; updates enqueue
   * UPDATE deltas. Both land in the same transaction as the record write.
   *
   * @param {Object} formData - The form data to save
   * @param {string} formId - The form type identifier (e.g. 'gas-safety-record')
   * @param {number|null} formInstanceId - DB id for updates, null for creates
   * @param {{tenantId?: string, userId?: string}} [identity] - active auth context
   */
  static async saveForm(formData, formId, formInstanceId = null, identity = {}) {
    try {
      await db.ensureReady();

      const tenantId = identity.tenantId || LOCAL_WORKSPACE_TENANT_ID;
      const now = new Date();

      if (formInstanceId) {
        const id = parseInt(formInstanceId);
        const existing = await db.forms.get(id);
        if (!existing) throw new Error('Form not found');

        // Immutability guard: issued certificates cannot be edited.
        assertMutable(existing);

        const mutationVersion = (existing.mutationVersion ?? 0) + 1;
        const cleanData = sanitizeDeep(formData);

        await db.transaction('rw', db.forms, db.syncQueue, async () => {
          await db.forms.update(id, {
            data: cleanData,
            status: formData.status || existing.status || 'draft',
            updatedAt: now,
            lastAccessedAt: now,
            mutationVersion,
            syncStatus: SYNC_STATUS.PENDING,
            $snapshot: existing.$snapshot ?? existing.data // merge ancestor
          });

          await outboxManager.enqueue({
            deltaId: uuid(),
            collection: 'forms',
            recordId: String(id),
            action: 'UPDATE',
            payload: {
              ...(await this._payloadFrom(id)),
              data: cleanData
            },
            identity: { tenantId, userId: identity.userId }
          });
        });

        const updated = await db.forms.get(id);
        updated.id = id;
        return updated;
      }

      // ---- CREATE path -------------------------------------------------
      const instanceId = `${formId}-${Date.now()}-${uuid().slice(0, 8)}`;
      const record = {
        formId,
        instanceId,
        data: sanitizeDeep(formData),
        status: formData.status || 'draft',
        createdAt: now,
        updatedAt: now,
        lastAccessedAt: now,
        tenantId,
        userId: identity.userId || null,
        syncStatus: SYNC_STATUS.PENDING,
        mutationVersion: 1,
        deletedAt: null
      };

      let newId;
      await db.transaction('rw', db.forms, db.syncQueue, async () => {
        newId = await db.forms.add(record);

        await outboxManager.enqueue({
          deltaId: uuid(),
          collection: 'forms',
          recordId: String(newId),
          action: 'CREATE',
          payload: { ...record, id: newId },
          identity: { tenantId, userId: identity.userId }
        });
      });

      return { ...record, id: newId };
    } catch (error) {
      console.error('FormService save error:', error);
      throw new Error(`Failed to save form: ${error.message}`);
    }
  }

  /** Read-back helper used to build full-record push payloads. */
  static async _payloadFrom(id) {
    const rec = await db.forms.get(id);
    if (!rec) throw new Error('Form vanished during transaction');
    return {
      id: rec.instanceId, // cross-device identity == PB record id
      form_type: rec.formId,
      certificate_number: rec.serialNumber ?? null,
      status: rec.status,
      data: rec.data,
      organization_id: rec.tenantId,
      client_updated_at: rec.updatedAt?.toISOString?.() ?? new Date().toISOString()
    };
  }

  /** Soft-delete + DELETE delta (tombstone survives offline windows). */
  static async deleteForm(formInstanceId, identity = {}) {
    try {
      await db.ensureReady();
      const id = parseInt(formInstanceId);
      const existing = await db.forms.get(id);
      if (!existing) return;

      assertMutable(existing); // issued certs cannot be deleted either

      await db.transaction('rw', db.forms, db.syncQueue, async () => {
        await db.forms.update(id, {
          deletedAt: new Date(),
          updatedAt: new Date(),
          syncStatus: SYNC_STATUS.PENDING
        });

        await outboxManager.enqueue({
          deltaId: uuid(),
          collection: 'forms',
          recordId: String(id),
          action: 'DELETE',
          identity: {
            tenantId: identity.tenantId || existing.tenantId || LOCAL_WORKSPACE_TENANT_ID,
            userId: identity.userId
          }
        });
      });
    } catch (error) {
      console.error('FormService delete error:', error);
      throw new Error(`Failed to delete form: ${error.message}`);
    }
  }

  /** Hard-remove a tombstoned record that was never pushed (local-only). */
  static async purgeLocalOnly(formInstanceId) {
    await db.ensureReady();
    const id = parseInt(formInstanceId);
    const record = await db.forms.get(id);
    if (!record) return;
    const hasPendingDelta = await db.syncQueue
      .where('[status+nextAttemptAt]')
      .between(['pending', db.constructor.minKey], ['pending', Date.now()])
      .and((d) => d.collection === 'forms' && d.recordId === String(id))
      .count();
    if (!hasPendingDelta && record.syncStatus !== SYNC_STATUS.PENDING) {
      throw new Error('Refusing to purge: record exists on server.');
    }
    await db.transaction('rw', db.forms, db.syncQueue, async () => {
      await db.syncQueue
        .where('recordId')
        .equals(String(id))
        .and((d) => d.collection === 'forms')
        .delete();
      await db.forms.delete(id);
    });
  }

  /**
   * Get a specific form by ID, tenant-scoped.
   * @returns {Promise<Object|null>}
   */
  static async getForm(formInstanceId, identity = {}) {
    try {
      await db.ensureReady();
      const tenantId = identity.tenantId || LOCAL_WORKSPACE_TENANT_ID;
      const form = await db.forms.get(parseInt(formInstanceId));

      // Tenant guard: a record outside the active tenant is treated as
      // non-existent (never leak existence via timing/error differences).
      if (!form || form.tenantId !== tenantId) return null;

      await db.forms.update(form.id, { lastAccessedAt: new Date() });
      return form;
    } catch (error) {
      console.error('FormService get error:', error);
      throw new Error(`Failed to retrieve form: ${error.message}`);
    }
  }

  /**
   * Recent forms for the active tenant, newest-first, excluding soft-deleted.
   */
  static async getRecentForms(limit = 10, identity = {}) {
    try {
      await db.ensureReady();
      const tenantId = identity.tenantId || LOCAL_WORKSPACE_TENANT_ID;

      const forms = await db.forms
        .where('[tenantId+updatedAt]')
        .between([tenantId, db.constructor.minKey], [tenantId, db.constructor.maxKey])
        .reverse()
        .limit(limit * 2)
        .toArray();

      return forms.filter((f) => !f.deletedAt).slice(0, limit);
    } catch (error) {
      console.error('FormService getRecent error:', error);
      return [];
    }
  }

  /** Dashboard stats scoped to the active tenant only. */
  static async getFormStats(identity = {}) {
    try {
      await db.ensureReady();
      const tenantId = identity.tenantId || LOCAL_WORKSPACE_TENANT_ID;

      const allForms = (
        await db.forms.where('[tenantId+status]').equalsAnyOf?.([
          [tenantId, 'draft'],
          [tenantId, 'completed'],
          [tenantId, 'archived'],
          [tenantId, 'issued']
        ])
      )?.filter((f) => !f.deletedAt) ??
        (await db.forms
          .where('[tenantId+updatedAt]')
          .between([tenantId, db.constructor.minKey], [tenantId, db.constructor.maxKey])
          .toArray()
        ).filter((f) => !f.deletedAt);

      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);

      return {
        total: allForms.length,
        drafts: allForms.filter((f) => f.status === 'draft').length,
        completed: allForms.filter((f) => f.status === 'completed').length,
        thisMonth: allForms.filter((f) => {
          const createdDate = f.createdAt ? new Date(f.createdAt) : null;
          return createdDate && createdDate >= thisMonth;
        }).length
      };
    } catch (error) {
      console.error('FormService stats error:', error);
      return { total: 0, drafts: 0, completed: 0, thisMonth: 0 };
    }
  }

  /**
   * Status transitions enforce the lifecycle: issued is terminal and can
   * only be reached from signed states; archived forms stay readable.
   */
  static async updateFormStatus(formInstanceId, status, identity = {}) {
    try {
      await db.ensureReady();
      const id = parseInt(formInstanceId);
      const existing = await db.forms.get(id);
      if (!existing) throw new Error('Form not found');

      assertMutable(existing);

      if (existing.status === 'issued' && status !== 'issued') {
        throw new Error('Issued certificates are immutable.');
      }

      const now = new Date();
      await db.transaction('rw', db.forms, db.syncQueue, async () => {
        await db.forms.update(id, {
          status,
          updatedAt: now,
          mutationVersion: (existing.mutationVersion ?? 0) + 1,
          syncStatus: SYNC_STATUS.PENDING
        });

        await outboxManager.enqueue({
          deltaId: uuid(),
          collection: 'forms',
          recordId: String(id),
          action: 'UPDATE',
          payload: await this._payloadFrom(id),
          identity: {
            tenantId: identity.tenantId || existing.tenantId,
            userId: identity.userId
          }
        });
      });
    } catch (error) {
      console.error('FormService updateStatus error:', error);
      throw new Error(`Failed to update form status: ${error.message}`);
    }
  }

  /** All forms of one type within the active tenant. */
  static async getFormsByType(formId, identity = {}) {
    try {
      await db.ensureReady();
      const tenantId = identity.tenantId || LOCAL_WORKSPACE_TENANT_ID;
      return (
        await db.forms.where('[tenantId+formId]').equals([tenantId, formId]).toArray()
      ).filter((f) => !f.deletedAt);
    } catch (error) {
      console.error('FormService getByType error:', error);
      return [];
    }
  }

  /** Tenant-scoped search across instanceId and serialized form data. */
  static async searchForms(searchTerm, identity = {}) {
    try {
      await db.ensureReady();
      const tenantId = identity.tenantId || LOCAL_WORKSPACE_TENANT_ID;
      const allForms = (
        await db.forms
          .where('[tenantId+updatedAt]')
          .between([tenantId, db.constructor.minKey], [tenantId, db.constructor.maxKey])
          .toArray()
      ).filter((f) => !f.deletedAt);

      const lowerSearch = searchTerm.toLowerCase();
      return allForms.filter((form) => {
        if (form.instanceId?.toLowerCase().includes(lowerSearch)) return true;
        if (form.serialNumber?.toLowerCase().includes(lowerSearch)) return true;
        if (form.data) {
          return JSON.stringify(form.data).toLowerCase().includes(lowerSearch);
        }
        return false;
      });
    } catch (error) {
      console.error('FormService search error:', error);
      return [];
    }
  }
}

export default FormService;
